import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Wind } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setErr("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-full bg-navy flex items-center justify-center"><Wind size={20} className="text-gold" /></div>
          <div className="font-display font-bold text-navy text-xl">Breathe<span className="text-gold">·</span>Thrive</div>
        </Link>
        <div className="bg-warm-white border border-edge rounded-3xl p-8 shadow-[0_8px_30px_rgba(44,45,91,0.06)]">
          <h1 className="font-display text-2xl font-bold text-navy">Choose a new password</h1>
          <p className="text-sm text-ink-mid mt-1">Set a new password for your Breathe to Thrive account.</p>
          {!token ? (
            <div className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="reset-no-token">
              This reset link is missing its token. Please request a fresh reset email.
            </div>
          ) : done ? (
            <div data-testid="reset-done" className="mt-6 text-sm bg-emerald-50 border border-emerald-300 rounded-xl px-4 py-3 text-ink-dark">
              <div className="font-display text-navy font-semibold mb-1">Password updated ✓</div>
              <div>Redirecting you to sign in…</div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-ink-soft font-semibold">New password</label>
                <input data-testid="reset-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl border border-edge bg-cream focus:outline-none focus:ring-2 focus:ring-gold text-ink-dark" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Confirm new password</label>
                <input data-testid="reset-confirm" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl border border-edge bg-cream focus:outline-none focus:ring-2 focus:ring-gold text-ink-dark" />
              </div>
              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="reset-error">{err}</div>}
              <button data-testid="reset-submit" type="submit" disabled={loading} className="w-full bg-navy text-warm-white px-5 py-3 rounded-full font-semibold hover:-translate-y-0.5 transition-all disabled:opacity-50">
                {loading ? "Updating…" : "Update password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
